/**
 * Every context the app needs, in one place: react-query, i18n (core + feature
 * dictionaries), theme, exam mode, auth and toasts. Tests render the same
 * provider stack so a component behaves identically in a test and in the app.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import type { Lang } from "@shared/api";
import { ToastProvider } from "@client/components/ui";
import { I18nProvider, en, mergeDictionaries, sv } from "@client/i18n";
import { AuthProvider } from "./auth";
import { ExamModeProvider } from "./examMode";
import { featureDictionaries } from "./features";
import { ThemeProvider } from "./theme";

/** Core dictionaries plus every feature's, merged once (spec §4.8). */
export const dictionaries = mergeDictionaries([{ sv, en }, ...featureDictionaries]);

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          const status = (error as { status?: number }).status ?? 0;
          if (status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

export interface AppProvidersProps {
  children: ReactNode;
  queryClient?: QueryClient;
  /**
   * Forces a language: tests, and `main.tsx` (`lang="sv"`, ruling 2026-09-28 —
   * the app is Swedish only while `LANGUAGE_SWITCH_ENABLED` is false).
   * Otherwise the stored/profile language wins.
   */
  lang?: Lang;
}

export function AppProviders({ children, queryClient, lang }: AppProvidersProps) {
  const client = useMemo(() => queryClient ?? createAppQueryClient(), [queryClient]);
  return (
    <QueryClientProvider client={client}>
      <I18nProvider dictionaries={dictionaries} {...(lang ? { lang } : {})}>
        <ThemeProvider>
          <AuthProvider>
            <ExamModeProvider>
              <ToastProvider>{children}</ToastProvider>
            </ExamModeProvider>
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
